// The Atlassian API catalog and its revision as the backend serves them (/api/catalog/*).
// Every reader guards the shape: a missing or older backend (and e2e catch-all mocks that
// answer `{}` / `[]`) must degrade to "nothing known", never crash the shell.

import { useEffect, useState } from 'react'

import type { Catalog } from '@/catalog/types'
import { api } from '@/lib/api'

export const EMPTY_CATALOG: Catalog = { generated_from: {}, products: [], warnings: [], checked_at: null }

const isObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v)

/** GET /api/catalog — anything without an array `products` is treated as an empty catalog. */
export async function fetchCatalog(): Promise<Catalog> {
  const raw = await api<unknown>('/catalog')
  if (!isObject(raw) || !Array.isArray(raw.products)) return EMPTY_CATALOG
  return {
    generated_from: isObject(raw.generated_from) ? (raw.generated_from as Record<string, string>) : {},
    products: raw.products as Catalog['products'],
    warnings: Array.isArray(raw.warnings) ? (raw.warnings as string[]) : [],
    checked_at: typeof raw.checked_at === 'string' ? raw.checked_at : null,
  }
}

export type EventKind = 'added' | 'deprecated' | 'undeprecated' | 'removed' | 'restored'

export type CatalogEvent = {
  id: number
  detected_at: string
  key: string
  product: string
  method: string
  path: string
  operation_id: string
  kind: EventKind
  /** found by the first check against the older snapshot, not as it happened */
  catch_up: boolean
  detail: string | null
}

/** Most events one request can return (the backend's EVENTS_MAX_LIMIT). */
export const EVENTS_MAX = 1000

export async function fetchCatalogEvents(filter: { kind?: string; product?: string; limit?: number }): Promise<CatalogEvent[]> {
  const q = new URLSearchParams()
  if (filter.kind) q.set('kind', filter.kind)
  if (filter.product) q.set('product', filter.product)
  if (filter.limit) q.set('limit', String(filter.limit))
  const qs = q.toString()
  const raw = await api<unknown>(`/catalog/events${qs ? `?${qs}` : ''}`)
  return Array.isArray(raw) ? (raw as CatalogEvent[]) : []
}

export type CatalogRun = {
  id: number
  started_at: string
  finished_at: string | null
  status: 'running' | 'ok' | 'partial' | 'failed' | 'interrupted'
  products: Record<string, string>
}

/** Latest check run, `null` when there has been none. */
export async function fetchLatestRun(): Promise<CatalogRun | null> {
  const raw = await api<unknown>('/catalog/runs/latest')
  return isObject(raw) && typeof raw.status === 'string' && typeof raw.started_at === 'string'
    ? (raw as CatalogRun)
    : null
}

/** POST /api/catalog/check: 'started', or 'busy' when a check already holds the lock (409). */
export async function startCatalogCheck(): Promise<'started' | 'busy'> {
  try {
    await api('/catalog/check', { method: 'POST' })
    return 'started'
  } catch (e) {
    // api() throws Error("<status> <body>")
    if (e instanceof Error && e.message.startsWith('409')) return 'busy'
    throw e
  }
}

export type ApiStatus = 'ok' | 'deprecated' | 'removed' | 'outside_catalog' | 'not_checked'

export type EndpointStatus = {
  /** endpoint key "<product> METHOD /path/{}" or, for an endpoint outside the catalog, "METHOD /path" */
  key: string
  deprecated: boolean
  removed_at: string | null
  /** raw `Sunset` header value seen on a real response */
  sunset: string | null
  removal_note: string | null
}

export type OpApiStatus = {
  status: ApiStatus
  reasons: string[]
  replacement: string | null
  endpoints: EndpointStatus[]
}

export async function fetchOperationsStatus(): Promise<Record<string, OpApiStatus>> {
  const raw = await api<unknown>('/catalog/operations-status')
  if (!isObject(raw)) return {}
  const out: Record<string, OpApiStatus> = {}
  for (const [id, v] of Object.entries(raw)) {
    if (isObject(v) && typeof v.status === 'string') {
      out[id] = {
        status: v.status as ApiStatus,
        reasons: Array.isArray(v.reasons) ? (v.reasons as string[]) : [],
        replacement: typeof v.replacement === 'string' ? v.replacement : null,
        endpoints: Array.isArray(v.endpoints) ? (v.endpoints as EndpointStatus[]) : [],
      }
    }
  }
  return out
}

/** Operation id → API status; empty until loaded or when the backend can't answer. */
export function useOperationsStatus(): Record<string, OpApiStatus> {
  const [statuses, setStatuses] = useState<Record<string, OpApiStatus>>({})
  useEffect(() => {
    let alive = true
    fetchOperationsStatus()
      .then((s) => {
        if (alive) setStatuses(s)
      })
      .catch(() => {})
    return () => {
      alive = false
    }
  }, [])
  return statuses
}

/** Only these two statuses are flagged to the user as a problem. */
export const isFlagged = (s: OpApiStatus | undefined): boolean =>
  s?.status === 'deprecated' || s?.status === 'removed'

/** "<product> METHOD /path" or "METHOD /path" → its parts. */
export function splitEndpointKey(key: string): { product: string | null; method: string; path: string } {
  const parts = key.trim().split(/\s+/)
  if (parts.length >= 3) return { product: parts[0], method: parts[1], path: parts.slice(2).join(' ') }
  if (parts.length === 2) return { product: null, method: parts[0], path: parts[1] }
  return { product: null, method: '', path: key }
}
